import type { FC } from 'react';
import { Button, Flex, Tag, Typography } from 'antd';
import {
  CloseOutlined,
  PlusOutlined,
  SwapOutlined,
  WarningOutlined,
} from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import type { FollowUpTicketRef } from '@evimo/components/maintenance/work-orders/WorkOrderKanbanPageContainer/types';

export interface FollowUpPanelProps {
  tickets: FollowUpTicketRef[];
  onMove: (ticket: FollowUpTicketRef) => void;
  onCreateNew: (ticket: FollowUpTicketRef) => void;
  onDismiss: () => void;
}

export const FollowUpPanel: FC<FollowUpPanelProps> = ({
  tickets,
  onMove,
  onCreateNew,
  onDismiss,
}) => {
  const { t } = useTranslation('maintenance');
  if (tickets.length === 0) return null;

  return (
    <div
      role="status"
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 10,
        flexWrap: 'wrap',
        background: '#fffbeb',
        border: '1px solid #fde68a',
        borderRadius: 10,
        padding: '8px 12px',
      }}
    >
      <WarningOutlined style={{ color: '#d97706', flexShrink: 0 }} />
      <Typography.Text strong style={{ color: '#92400e', fontSize: 13 }}>
        {t('workOrders.kanban.followUp.title')}
      </Typography.Text>
      <Typography.Text type="secondary" style={{ fontSize: 12 }}>
        {t('workOrders.kanban.followUp.count', { count: tickets.length })}
      </Typography.Text>

      <Flex gap={6} wrap="wrap" style={{ flex: 1, minWidth: 0 }}>
        {tickets.map((ticket) => (
          <Tag
            key={ticket.ticketId}
            style={{
              margin: 0,
              background: '#fff',
              border: '1px solid #fcd34d',
              borderRadius: 6,
              paddingInline: 6,
              display: 'inline-flex',
              alignItems: 'center',
              gap: 4,
            }}
          >
            <span style={{ fontWeight: 600 }}>{ticket.ticketCode}</span>
            <Button
              type="link"
              size="small"
              icon={<PlusOutlined />}
              onClick={() => onCreateNew(ticket)}
              style={{ padding: 0, height: 'auto', fontSize: 12 }}
            >
              {t('workOrders.kanban.followUp.createNewShort')}
            </Button>
            <Button
              type="link"
              size="small"
              icon={<SwapOutlined />}
              onClick={() => onMove(ticket)}
              style={{ padding: 0, height: 'auto', fontSize: 12 }}
            >
              {t('workOrders.kanban.followUp.moveShort')}
            </Button>
          </Tag>
        ))}
      </Flex>

      <Button
        type="text"
        size="small"
        aria-label={t('workOrders.kanban.followUp.dismiss')}
        icon={<CloseOutlined />}
        onClick={onDismiss}
        style={{ color: '#92400e', marginLeft: 'auto' }}
      />
    </div>
  );
};
